import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'services/login/hooks';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import { useGetProjectsQuery } from 'services/projects/api';
import {
  useAddTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useGetTaskCommentsQuery,
  useGetTaskEventsQuery,
  usePatchTaskCommentMutation,
} from 'services/tasks/api';
import { Task } from 'services/tasks/types';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';
import Discussion from 'components/comments/Discussion';
import { summarizeTaskEvent } from './formatTaskHistoryEvent';

interface TaskDiscussionProps {
  task: Task;
}

const TaskDiscussion = ({
  task,
}: TaskDiscussionProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, user);
  const { data: comments, isLoading: commentsLoading } = useGetTaskCommentsQuery(task.id ?? '', {
    skip: !task.id,
  });
  const { data: events, isLoading: eventsLoading } = useGetTaskEventsQuery(task.id ?? '', {
    skip: !task.id,
  });
  const { data: projects } = useGetProjectsQuery();
  const [addComment] = useAddTaskCommentMutation();
  const [patchComment] = usePatchTaskCommentMutation();
  const [deleteComment] = useDeleteTaskCommentMutation();

  if (!task.id) {
    return null;
  }

  return (
    <Discussion
      comments={comments}
      currentUserId={user?.id}
      isLoading={commentsLoading || eventsLoading}
      onCreate={comment => addComment({ comment, taskId: task.id as string }).unwrap()}
      onDelete={id => deleteComment({ id, taskId: task.id as string }).unwrap()}
      onUpdate={(id, comment) => patchComment({
        comment,
        id,
        taskId: task.id as string,
      }).unwrap()}
      updates={(events ?? []).map(event => ({
        createdAt: event.createdAt,
        id: event.id,
        summary: summarizeTaskEvent(event, t, {
          projectName: projectId => projects?.find(project => project.id === projectId)?.name,
          userName: userId => {
            const match = users.find(person => person.id === userId);
            return match ? assigneeDisplayName(match) : undefined;
          },
        }),
      }))}
    />
  );
};

export default TaskDiscussion;
